import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import type { Watchlist, WatchlistInsert, WatchlistUpdate, WatchlistStock } from '../types/database';
import type { StockQuote } from '../types/market';
import { TradierService } from './TradierService';
import { CacheService } from './CacheService';

export interface WatchlistWithQuotes extends Watchlist {
  quotes: StockQuote[];
}

export interface OptimisticUpdate<T> {
  id: string;
  data: T;
  timestamp: number;
  pending: boolean;
}

export class WatchlistService {
  private static tradierService: TradierService;
  private static cacheService: CacheService;
  private static optimisticUpdates = new Map<string, OptimisticUpdate<Watchlist>>();

  // Initialize services
  static {
    this.tradierService = new TradierService();
    this.cacheService = new CacheService();
  }

  /**
   * Get all watchlists for a user with real-time quotes
   */
  static async getUserWatchlists(userId: string, includeQuotes = true): Promise<WatchlistWithQuotes[]> {
    const cacheKey = `watchlists:${userId}`;
    
    // Check cache first
    const cached = await this.cacheService.get<Watchlist[]>(cacheKey);
    let watchlists = cached;

    if (!watchlists) {
      // Fetch from database
      const { data, error } = await supabase
        .from('watchlists')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error fetching watchlists', error);
        throw new Error(`Failed to fetch watchlists: ${error.message}`);
      }

      watchlists = data || [];
      
      // Cache the result
      await this.cacheService.set(cacheKey, watchlists, 300); // 5 minutes
    }

    // Apply optimistic updates
    const watchlistsWithOptimistic = this.applyOptimisticUpdates(watchlists);

    if (!includeQuotes) {
      return watchlistsWithOptimistic.map(w => ({ ...w, quotes: [] }));
    }

    // Fetch quotes for all stocks in all watchlists
    const allTickers = new Set<string>();
    watchlistsWithOptimistic.forEach(watchlist => {
      watchlist.stocks.forEach(stock => {
        const ticker = typeof stock === 'string' ? stock : stock.ticker;
        allTickers.add(ticker);
      });
    });

    const quotes = allTickers.size > 0 
      ? await this.tradierService.getQuotes(Array.from(allTickers)) || []
      : [];

    // Create quote lookup map
    const quoteMap = new Map(quotes.map(quote => [quote.symbol, quote]));

    // Attach quotes to watchlists
    return watchlistsWithOptimistic.map(watchlist => ({
      ...watchlist,
      quotes: watchlist.stocks
        .map(stock => {
          const ticker = typeof stock === 'string' ? stock : stock.ticker;
          return quoteMap.get(ticker);
        })
        .filter((quote): quote is StockQuote => quote !== undefined)
    }));
  }

  /**
   * Get a specific watchlist by ID with quotes
   */
  static async getWatchlist(watchlistId: string, includeQuotes = true): Promise<WatchlistWithQuotes | null> {
    const cacheKey = `watchlist:${watchlistId}`;
    
    // Check cache first
    let watchlist = await this.cacheService.get<Watchlist>(cacheKey);

    if (!watchlist) {
      const { data, error } = await supabase
        .from('watchlists')
        .select('*')
        .eq('id', watchlistId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Not found
        }
        logger.error('Error fetching watchlist', error);
        throw new Error(`Failed to fetch watchlist: ${error.message}`);
      }

      watchlist = data;
      await this.cacheService.set(cacheKey, watchlist, 300);
    }

    // Apply optimistic updates
    const optimisticUpdate = this.optimisticUpdates.get(watchlistId);
    if (optimisticUpdate && optimisticUpdate.pending) {
      watchlist = { ...watchlist, ...optimisticUpdate.data };
    }

    if (!includeQuotes || !watchlist || !watchlist.stocks.length) {
      return { ...watchlist!, quotes: [] } as any;
    }

    // Extract tickers from stocks (handle both string and object formats)
    const tickers = watchlist.stocks.map(stock => 
      typeof stock === 'string' ? stock : stock.ticker
    );

    // Fetch quotes
    const quotes = await this.tradierService.getQuotes(tickers) || [];

    return {
      ...watchlist,
      quotes
    };
  }

  /**
   * Create a new watchlist with optimistic updates
   */
  static async createWatchlist(watchlistData: WatchlistInsert): Promise<Watchlist> {
    // Generate temporary ID for optimistic update
    const tempId = `temp_${Date.now()}`;
    const optimisticWatchlist: Watchlist = {
      id: tempId,
      ...watchlistData,
      stocks: watchlistData.stocks || [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Apply optimistic update
    this.optimisticUpdates.set(tempId, {
      id: tempId,
      data: optimisticWatchlist,
      timestamp: Date.now(),
      pending: true
    });

    try {
      // Perform actual database operation
      const { data, error } = await supabase
        .from('watchlists')
        .insert(watchlistData)
        .select()
        .single();

      if (error) {
        // Remove optimistic update on error
        this.optimisticUpdates.delete(tempId);
        logger.error('Error creating watchlist', error);
        throw new Error(`Failed to create watchlist: ${error.message}`);
      }

      // Replace optimistic update with real data
      this.optimisticUpdates.delete(tempId);
      
      // Invalidate cache
      await this.cacheService.delete(`watchlists:${watchlistData.user_id}`);

      return data;
    } catch (error) {
      this.optimisticUpdates.delete(tempId);
      throw error;
    }
  }

  /**
   * Update a watchlist with optimistic updates
   */
  static async updateWatchlist(
    watchlistId: string, 
    updates: WatchlistUpdate
  ): Promise<Watchlist> {
    // Get current watchlist for optimistic update
    const current = await this.getWatchlist(watchlistId, false);
    if (!current) {
      throw new Error('Watchlist not found');
    }

    // Apply optimistic update
    const optimisticWatchlist = { ...current, ...updates, updated_at: new Date().toISOString() };
    this.optimisticUpdates.set(watchlistId, {
      id: watchlistId,
      data: optimisticWatchlist,
      timestamp: Date.now(),
      pending: true
    });

    try {
      // Perform actual database operation
      const { data, error } = await supabase
        .from('watchlists')
        .update(updates)
        .eq('id', watchlistId)
        .select()
        .single();

      if (error) {
        // Remove optimistic update on error
        this.optimisticUpdates.delete(watchlistId);
        logger.error('Error updating watchlist', error);
        throw new Error(`Failed to update watchlist: ${error.message}`);
      }

      // Remove optimistic update
      this.optimisticUpdates.delete(watchlistId);
      
      // Invalidate cache
      await this.cacheService.delete(`watchlist:${watchlistId}`);
      await this.cacheService.delete(`watchlists:${data.user_id}`);

      return data;
    } catch (error) {
      this.optimisticUpdates.delete(watchlistId);
      throw error;
    }
  }

  /**
   * Delete a watchlist
   */
  static async deleteWatchlist(watchlistId: string): Promise<boolean> {
    // Get watchlist for cache invalidation
    const watchlist = await this.getWatchlist(watchlistId, false);
    if (!watchlist) {
      return false;
    }

    const { error } = await supabase
      .from('watchlists')
      .delete()
      .eq('id', watchlistId);

    if (error) {
      logger.error('Error deleting watchlist', error);
      throw new Error(`Failed to delete watchlist: ${error.message}`);
    }

    // Invalidate cache
    await this.cacheService.delete(`watchlist:${watchlistId}`);
    await this.cacheService.delete(`watchlists:${watchlist.user_id}`);

    return true;
  }

  /**
   * Add a stock to a watchlist with optimistic updates
   */
  static async addStockToWatchlist(watchlistId: string, ticker: string, name: string): Promise<Watchlist> {
    const watchlist = await this.getWatchlist(watchlistId, false);
    if (!watchlist) {
      throw new Error('Watchlist not found');
    }

    // Check if stock already exists (handle both string and object formats)
    const stockExists = watchlist.stocks.some(stock => {
      const stockTicker = typeof stock === 'string' ? stock : stock.ticker;
      return stockTicker === ticker;
    });

    if (stockExists) {
      throw new Error('Stock already in watchlist');
    }

    // Validate ticker format
    if (!/^[A-Z]{1,5}$/.test(ticker)) {
      throw new Error('Invalid ticker symbol');
    }

    const newStock: WatchlistStock = { ticker, name };
    const updatedStocks = [...watchlist.stocks, newStock];
    return this.updateWatchlist(watchlistId, { stocks: updatedStocks });
  }

  /**
   * Remove a stock from a watchlist with optimistic updates
   */
  static async removeStockFromWatchlist(watchlistId: string, ticker: string): Promise<Watchlist> {
    const watchlist = await this.getWatchlist(watchlistId, false);
    if (!watchlist) {
      throw new Error('Watchlist not found');
    }

    const updatedStocks = watchlist.stocks.filter(stock => {
      const stockTicker = typeof stock === 'string' ? stock : stock.ticker;
      return stockTicker !== ticker;
    });
    return this.updateWatchlist(watchlistId, { stocks: updatedStocks });
  }

  /**
   * Reorder stocks in a watchlist
   */
  static async reorderStocks(watchlistId: string, orderedStocks: WatchlistStock[]): Promise<Watchlist> {
    const watchlist = await this.getWatchlist(watchlistId, false);
    if (!watchlist) {
      throw new Error('Watchlist not found');
    }

    // Validate that all stocks in the new order exist in the current watchlist
    const currentTickers = new Set(watchlist.stocks.map(stock => 
      typeof stock === 'string' ? stock : stock.ticker
    ));
    const newTickers = new Set(orderedStocks.map(stock => 
      typeof stock === 'string' ? stock : stock.ticker
    ));
    
    if (currentTickers.size !== newTickers.size || 
        !orderedStocks.every(stock => {
          const ticker = typeof stock === 'string' ? stock : stock.ticker;
          return currentTickers.has(ticker);
        })) {
      throw new Error('Invalid stock order - stocks must match current watchlist');
    }

    return this.updateWatchlist(watchlistId, { stocks: orderedStocks });
  }

  /**
   * Get watchlist performance summary
   */
  static async getWatchlistPerformance(watchlistId: string): Promise<{
    totalStocks: number;
    gainers: number;
    losers: number;
    avgChange: number;
    totalVolume: number;
  }> {
    const watchlist = await this.getWatchlist(watchlistId, true);
    if (!watchlist || !watchlist.quotes.length) {
      return {
        totalStocks: 0,
        gainers: 0,
        losers: 0,
        avgChange: 0,
        totalVolume: 0
      };
    }

    const gainers = watchlist.quotes.filter(q => q.change > 0).length;
    const losers = watchlist.quotes.filter(q => q.change < 0).length;
    const avgChange = watchlist.quotes.reduce((sum, q) => sum + q.change_percent, 0) / watchlist.quotes.length;
    const totalVolume = watchlist.quotes.reduce((sum, q) => sum + q.volume, 0);

    return {
      totalStocks: watchlist.quotes.length,
      gainers,
      losers,
      avgChange,
      totalVolume
    };
  }

  /**
   * Apply optimistic updates to watchlists
   */
  private static applyOptimisticUpdates(watchlists: Watchlist[]): Watchlist[] {
    const result = [...watchlists];
    
    // Apply pending optimistic updates
    this.optimisticUpdates.forEach((update, id) => {
      if (update.pending) {
        const index = result.findIndex(w => w.id === id);
        if (index >= 0) {
          result[index] = { ...result[index], ...update.data };
        } else if (id.startsWith('temp_')) {
          // Add new optimistic watchlist
          result.unshift(update.data);
        }
      }
    });

    // Clean up old optimistic updates (older than 30 seconds)
    const now = Date.now();
    this.optimisticUpdates.forEach((update, id) => {
      if (now - update.timestamp > 30000) {
        this.optimisticUpdates.delete(id);
      }
    });

    return result;
  }

  /**
   * Clear all optimistic updates (useful for testing)
   */
  static clearOptimisticUpdates(): void {
    this.optimisticUpdates.clear();
  }

  /**
   * Subscribe to real-time watchlist changes
   */
  static subscribeToWatchlistChanges(
    userId: string, 
    callback: (payload: any) => void
  ): () => void {
    const channel = supabase
      .channel(`watchlists:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'watchlists',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          // Invalidate cache on changes
          this.cacheService.delete(`watchlists:${userId}`);
          if ((payload.new as any)?.id) {
            this.cacheService.delete(`watchlist:${(payload.new as any).id}`);
          }
          callback(payload);
        }
      )
      .subscribe();

    // Return unsubscribe function
    return () => {
      supabase.removeChannel(channel);
    };
  }
}